import React, { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { moduleChartStore } from "../../store/moduleChartStore";
import { inplantsStore } from "../../features/inplants/store/inplantsStore";
import { useStationsInit } from "../../components/dashbords/hooks/useStationsInit";
import { MouseWheelContainer } from "../../components/dashbords/MouseWheelContainer";
import { CursorRuler } from "../../components/dashbords/CursorRuler/CursorRuler";
import { PhaseChart } from "../../components/dashbords/charts/PhaseChart";
import { StationsChart } from "../../components/dashbords/charts/StationsChart";
import { RetroChart } from "../../components/dashbords/charts/RetroChart";
import ButtonComponent from "../../components/buttons/ButtonComponent";
import {
  MODULE_CHART_CONFIG,
  PHASE_COLORS,
  STATION_COLORS,
  getSeriesColor,
  ModuleChartConfigEntry,
} from "../../components/dashbords/config/moduleChartConfig";
import { ChartState } from "../../components/dashbords/states/ChartState";
import { isRetroChartEmpty } from "../../models/chartData";
import "./styles.css";

type DeviceTypeParam = "mdbn" | "mmb" | "smb";

const calculateLimit = (max: number) => Math.ceil((max + 10) / 20) * 20;

const ModuleChartDashboard: React.FC = observer(() => {
  const { type, id, charge_id } = useParams<{
    type: string;
    id: string;
    charge_id: string;
    line_name: string;
  }>();
  const navigate = useNavigate();

  const deviceType = (type as DeviceTypeParam) || "mdbn";
  const config = MODULE_CHART_CONFIG[deviceType] || [];

  const { stationLabels } = useStationsInit(
    id,
    deviceType === "smb" ? "sub_devices" : "stations",
  );

  useEffect(() => {
    localStorage.setItem(
      "selectedMonth",
      moduleChartStore.selectedMonth.toISOString(),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleChartStore.selectedMonth]);

  useEffect(() => {
    if (id) {
      moduleChartStore.load(Number(id), deviceType);
    }
    return () => moduleChartStore.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, deviceType]);

  const data = moduleChartStore.data;

  const maxDate = useMemo(() => {
    if (!moduleChartStore.loadedTo) return undefined;
    return Math.min(new Date(moduleChartStore.loadedTo).getTime(), Date.now());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleChartStore.loadedTo]);

  // Drill-down MMB → SMB (headerExtra для sub_devices)
  const smbLinks = useMemo(() => {
    if (deviceType !== "mmb") return null;
    const subDevicesChart = moduleChartStore.getChart("sub_devices");
    if (!subDevicesChart) return null;

    const smbNames = Array.from(
      new Set(
        subDevicesChart.series.map((series) => series.name.split(" ")[0]),
      ),
    ).filter(Boolean);

    const links = smbNames
      .map((name) => {
        const module = inplantsStore.modules.find(
          (item) => item.charge_id === name,
        );
        return module
          ? {
              name,
              moduleId: module.id,
              chargeId: module.charge_id,
              lineName: (module as any).line_name || "",
            }
          : null;
      })
      .filter(Boolean) as {
      name: string;
      moduleId: number;
      chargeId: string;
      lineName: string;
    }[];

    if (links.length === 0) return null;

    return (
      <span className="chart-title-extra">
        {links.map((link) => (
          <button
            key={link.name}
            type="button"
            className="smb-link-button"
            onClick={() =>
              navigate(
                `/module-charts/smb/${link.moduleId}/${link.chargeId}/${link.lineName}`,
              )
            }
          >
            {link.name}
          </button>
        ))}
      </span>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceType, navigate, data]);

  const renderChart = (entry: ModuleChartConfigEntry) => {
    const chart = moduleChartStore.getChart(entry.id);
    if (!chart || !data) return null;

    const title = chart.title;
    const activeSeries = moduleChartStore.activeSeries[entry.id] || {};

    // Базовые пропсы, общие для всех графиков
    const commonChartProps = {
      title,
      chart,
      activeSeries,
      maxDate,
      onCursorMove: (time: number | null) => moduleChartStore.setCursorPoint(time),
    };

    // Синхронизированный зум — только для МДБН (phase/stations), как было раньше.
    // Для ММБ/СМБ (series) графики зумятся независимо, без общей синхронизации.
    const isMdbn = deviceType === "mdbn";
    const syncZoomProps = isMdbn
      ? {
          zoomStart: moduleChartStore.zoomStart,
          zoomEnd: moduleChartStore.zoomEnd,
          zoomResetVersion: moduleChartStore.zoomResetVersion,
          onDataZoom: (params: any) => moduleChartStore.handleDataZoom(params),
        }
      : {};

    let body: React.ReactNode;

    if (entry.render === "stations") {
      const colors: Record<string, string> = {};
      chart.series.forEach((series) => {
        const color = getSeriesColor(series.name, STATION_COLORS);
        if (color) {
          const num = series.name.match(/(\d+)$/)?.[1] ?? series.name;
          colors[num] = color;
        }
      });
      body = (
        <StationsChart
          {...commonChartProps}
          {...syncZoomProps}
          labels={stationLabels}
          activeStations={activeSeries}
          onToggleStation={(stationId, selected) =>
            moduleChartStore.toggleSeries(chart.id, stationId, selected)
          }
          dynamicLevel={40}
          colors={colors}
        />
      );
    } else if (entry.render === "phase") {
      const colors: Record<string, string> = {};
      chart.series.forEach((series) => {
        const color = getSeriesColor(series.name, PHASE_COLORS);
        if (color) colors[series.name] = color;
      });
      // Динамический уровень шкалы Y по данным графика
      const maxValue = chart.series.reduce((acc, series) => {
        let seriesMax = 0;
        series.segments.forEach((segment) => {
          if (segment.type !== "online") return;
          segment.data.forEach((point) => {
            if (point.value > seriesMax) seriesMax = point.value;
          });
        });
        return Math.max(acc, seriesMax);
      }, 0);
      body = (
        <PhaseChart
          {...commonChartProps}
          {...syncZoomProps}
          colors={colors}
          onToggleSeries={(name, selected) =>
            moduleChartStore.toggleSeries(chart.id, name, selected)
          }
          dynamicLevel={calculateLimit(maxValue)}
          limits={data.limits}
          limitKey={entry.limitKey}
        />
      );
    } else {
      // render === "series" (ММБ / СМБ)
      const colors: Record<string, string> = {};
      chart.series.forEach((series) => {
        const color = getSeriesColor(series.name, PHASE_COLORS);
        if (color) colors[series.name] = color;
      });
      body = (
        <RetroChart
          {...commonChartProps}
          colors={colors}
          onToggleSeries={(name) => moduleChartStore.toggleSeries(chart.id, name)}
          limits={data.limits}
          limitKey={entry.limitKey}
          headerExtra={
            entry.id === "sub_devices" && deviceType === "mmb" ? smbLinks : undefined
          }
        />
      );
    }

    return (
      <ChartState loading={false} error={null} empty={isRetroChartEmpty(chart)}>
        {body}
      </ChartState>
    );
  };

  const moduleDisplay =
    deviceType === "mdbn"
      ? `МДБН ${charge_id}`
      : deviceType === "mmb"
        ? `ММБ ${charge_id}`
        : `СМБ ${charge_id}`;

  const currentMonth = `${new Date().getFullYear()}-${String(
    new Date().getMonth() + 1,
  ).padStart(2, "0")}`;

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <div className="header-title-group">
          <span className="module-display">{moduleDisplay}</span>
        </div>

        <div className="header-nav-group">
          <ButtonComponent
            title="←"
            onClick={() => moduleChartStore.shiftPeriod("prev")}
          />

          <input
            type="month"
            min="2025-01"
            max={currentMonth}
            value={`${moduleChartStore.selectedMonth.getFullYear()}-${String(
              moduleChartStore.selectedMonth.getMonth() + 1,
            ).padStart(2, "0")}`}
            onChange={(e) => {
              const newDate = new Date(e.target.value);
              moduleChartStore.setSelectedMonth(newDate);
            }}
          />
          <ButtonComponent
            title="→"
            onClick={() => moduleChartStore.shiftPeriod("next")}
          />
          <div className="resolution-indicator">
            <span
              className={`resolution-badge ${moduleChartStore.detailLevel}`}
            >
              {moduleChartStore.detailLevel === "month" && "Месяц"}
              {moduleChartStore.detailLevel === "week" && "Неделя"}
              {moduleChartStore.detailLevel === "day" && "День"}
            </span>
          </div>
        </div>

        <div className="header-actions-group">
          <button
            type="button"
            className="simple-button"
            onClick={() => void moduleChartStore.refresh()}
          >
            Обновить
          </button>
          <ButtonComponent title="Назад" onClick={() => navigate(-1)} />
        </div>
      </div>

      <div className="main-layout">
        <div style={{ position: "relative" }}>
          <CursorRuler enabled={false} />

          <MouseWheelContainer
            step={2}
            onDelta={(delta) => moduleChartStore.handleWheelDelta(delta)}
            className="charts-container"
          >
            <div className="scroll-value-indicator">
              {moduleChartStore.wheelValue}
            </div>

            {moduleChartStore.loading && (
              <div className="loading-overlay">
                <div className="loading-spinner">Загрузка...</div>
              </div>
            )}

            {moduleChartStore.error ? (
              <div className="chart-state chart-state--error">
                <div>Ошибка: {moduleChartStore.error}</div>
                <button
                  type="button"
                  className="simple-button"
                  onClick={() => void moduleChartStore.refresh()}
                >
                  Повторить
                </button>
              </div>
            ) : (
              config.map((entry) => (
                <div key={entry.id}>{renderChart(entry)}</div>
              ))
            )}
          </MouseWheelContainer>
        </div>
      </div>
    </div>
  );
});

export default ModuleChartDashboard;
